import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { contactInfo, formspreeEndpoint } from "../mock";

const Field = ({ label, name, value, onChange, type = "text", required, placeholder }) => (
  <div className="flex flex-col gap-2">
    <label
      htmlFor={name}
      className="text-[11px] uppercase tracking-[0.25em] text-white/70"
    >
      {label}
      {required && <span className="text-white/50">*</span>}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className="w-full bg-transparent border-b border-white/25 text-white placeholder:text-white/40 px-0 py-3 text-[15px] focus:outline-none focus:border-[#9F8B76] transition-colors"
    />
  </div>
);

const Contact = () => {
  const [form, setForm] = useState({ nom: "", email: "", ville: "", sujet: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.email || !form.message) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setSending(true);
    try {
      const payload = new FormData();
      payload.append("Nom", form.nom);
      payload.append("Email", form.email);
      payload.append("Ville", form.ville);
      payload.append("Sujet", form.sujet);
      payload.append("Message", form.message);
      payload.append("_replyto", form.email);
      payload.append("_subject", `Nouveau contact Maison Labroca — ${form.sujet || "Sans sujet"}`);

      const res = await fetch(formspreeEndpoint, {
        method: "POST",
        body: payload,
        headers: { Accept: "application/json" },
      });

      if (res.ok) {
        toast.success("Merci ! Votre message a bien été envoyé.");
        setForm({ nom: "", email: "", ville: "", sujet: "", message: "" });
      } else {
        const data = await res.json().catch(() => ({}));
        const err =
          (data && data.errors && data.errors[0] && data.errors[0].message) ||
          "Une erreur est survenue. Réessayez plus tard.";
        toast.error(err);
      }
    } catch (err) {
      toast.error("Impossible d'envoyer votre message. Vérifiez votre connexion.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-[#111111] text-white py-24 md:py-32" data-nav-theme="dark">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-14 md:gap-20">
        <div className="md:col-span-5">
          <h2
            className="font-display uppercase leading-[0.95]"
            style={{ fontSize: "clamp(34px, 4.5vw, 76px)" }}
          >
            Parlez-nous de votre projet
          </h2>
          <p className="mt-6 text-white/75 text-[15px] leading-relaxed max-w-md">
            Un projet de construction, de rénovation ou de programme
            immobilier en Île-de-France ? Écrivez-nous, nous vous répondons
            sous 48 heures.
          </p>
          <div className="mt-10 space-y-5 text-white/80">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-white/50">Email</p>
              <a
                href={`mailto:${contactInfo.email}`}
                className="text-[15px] hover:text-[#9F8B76] transition-colors break-all"
              >
                {contactInfo.email}
              </a>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-white/50">Siège social</p>
              <p className="text-[15px]">{contactInfo.location}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-white/50">Zone d'intervention</p>
              <p className="text-[15px]">{contactInfo.zone}</p>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="md:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <Field label="Nom" name="nom" required value={form.nom} onChange={onChange} placeholder="Votre nom" />
          <Field label="Adresse mail" name="email" required type="email" value={form.email} onChange={onChange} placeholder="Votre mail" />
          <Field label="Ville" name="ville" value={form.ville} onChange={onChange} placeholder="Votre ville" />
          <Field label="Sujet" name="sujet" value={form.sujet} onChange={onChange} placeholder="Sujet de la demande" />
          <div className="md:col-span-2 flex flex-col gap-2">
            <label htmlFor="message" className="text-[11px] uppercase tracking-[0.25em] text-white/70">
              Message<span className="text-white/50">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              value={form.message}
              onChange={onChange}
              rows={5}
              placeholder="Votre message"
              className="w-full bg-transparent border-b border-white/25 text-white placeholder:text-white/40 px-0 py-3 text-[15px] focus:outline-none focus:border-[#9F8B76] transition-colors resize-none"
            />
          </div>
          <div className="md:col-span-2 flex justify-start pt-2">
            <button
              type="submit"
              disabled={sending}
              className="inline-flex items-center gap-3 bg-white text-neutral-900 rounded-full pl-5 pr-1.5 py-1.5 text-[12px] uppercase tracking-widest font-medium hover:bg-[#9F8B76] hover:text-white transition-colors group disabled:opacity-70 disabled:cursor-not-allowed active:scale-95"
            >
              {sending ? "Envoi en cours" : "Envoyer"}
              <span className="w-9 h-9 rounded-full bg-[#9F8B76] text-white flex items-center justify-center">
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
              </span>
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Contact;
